import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة المولارية - حساب التركيز المولي والمحلول الكيميائي";
const DESCRIPTION =
  "حاسبة المولارية المجانية - احسب التركيز المولي، وزن المادة، والحجم بدقة. أداة سهلة للكيميائيين والطلاب لحساب تركيز المحاليل الكيميائية مع شرح للقوانين والأمثلة.";
const KEYWORDS =
  "حاسبة المولارية, حساب التركيز المولي, قانون المولارية, حساب وزن المادة الكيميائية, molarity calculator, حساب المولات, تركيز المحاليل";
const OG_URL = "https://do-calculate.com/calculator/ar/molarity/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة المولارية",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "ScientificApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة المولارية",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو قانون المولارية؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "المولارية (M) تساوي عدد مولات المذاب مقسوماً على حجم المحلول باللتر. الصيغة الرياضية هي: M = n / V.",
        },
      },
      {
        "@type": "Question",
        name: "كيف يتم حساب وزن المادة اللازم لتحضير محلول؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "الوزن (جم) = المولارية (مول/لتر) × الحجم (لتر) × الوزن الجزيئي (جم/مول).",
        },
      },
    ],
  },
];

function MolarityCalculator() {
  const [molarity, setMolarity] = useState("");
  const [molecularWeight, setMolecularWeight] = useState("");
  const [volume, setVolume] = useState("");
  const [mass, setMass] = useState("");
  const [volumeUnit, setVolumeUnit] = useState("L");
  const [massUnit, setMassUnit] = useState("g");
  const [molarityUnit, setMolarityUnit] = useState("M");

  const [calculationMode, setCalculationMode] = useState("mass"); // mass, molarity, volume
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculate = () => {
    setError("");
    const M = parseFloat(molarity);
    const MW = parseFloat(molecularWeight);
    const V = parseFloat(volume);
    const m = parseFloat(mass);

    // Convert Volume to Liters
    const getVolInL = (val) => {
      if (volumeUnit === "mL") return val / 1000;
      if (volumeUnit === "uL") return val / 1000000;
      return val;
    };

    // Convert Mass to Grams
    const getMassInG = (val) => {
      if (massUnit === "mg") return val / 1000;
      if (massUnit === "ug") return val / 1000000;
      if (massUnit === "kg") return val * 1000;
      return val;
    };

    // Convert Molarity to M (mol/L)
    const getMolInM = (val) => {
      if (molarityUnit === "mM") return val / 1000;
      if (molarityUnit === "uM") return val / 1000000;
      if (molarityUnit === "nM") return val / 1000000000;
      return val;
    };

    try {
      if (calculationMode === "mass") {
        if (!molarity || !molecularWeight || !volume) return;
        const resMassG = getMolInM(M) * MW * getVolInL(V);
        setResult({
          value: resMassG,
          unit: "جم",
          details: `الكتلة = ${M} ${molarityUnit} × ${MW} جم/مول × ${V} ${volumeUnit}`,
        });
      } else if (calculationMode === "molarity") {
        if (!mass || !molecularWeight || !volume) return;
        const resM = getMassInG(m) / (MW * getVolInL(V));
        setResult({
          value: resM,
          unit: "M (مول/لتر)",
          details: `المولارية = ${m} ${massUnit} / (${MW} جم/مول × ${V} ${volumeUnit})`,
        });
      } else if (calculationMode === "volume") {
        if (!mass || !molarity || !molecularWeight) return;
        const resV = getMassInG(m) / (getMolInM(M) * MW);
        setResult({
          value: resV,
          unit: "لتر",
          details: `الحجم = ${m} ${massUnit} / (${M} ${molarityUnit} × ${MW} جم/مول)`,
        });
      }
    } catch (err) {
      setError("يرجى التأكد من إدخال قيم صحيحة.");
    }
  };

  useEffect(() => {
    calculate();
  }, [molarity, molecularWeight, volume, mass, volumeUnit, massUnit, molarityUnit, calculationMode]);

  const handleClear = () => {
    setMolarity("");
    setMolecularWeight("");
    setVolume("");
    setMass("");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة المولارية</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تساعدك <strong>حاسبة المولارية</strong> في حساب تركيز المحاليل الكيميائية بسرعة ودقة. يمكنك حساب الكتلة المطلوبة لتحضير محلول، أو معرفة المولارية الناتجة، أو تحديد الحجم اللازم. أداة مثالية للمختبرات العلمية والطلاب.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>أدخل البيانات للحساب</h2>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <label >ما الذي تريد حسابه؟</label>
                  <select className="form-select" value={calculationMode} onChange={(e) => { setCalculationMode(e.target.value); handleClear(); }}>
                    <option value="mass">الكتلة المطلوبة (جم)</option>
                    <option value="molarity">المولارية (M)</option>
                    <option value="volume">الحجم المطلوب (L)</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label >الوزن الجزيئي (جم/مول)</label>
                  <input type="number" className="form-control" value={molecularWeight} onChange={(e) => setMolecularWeight(e.target.value)} placeholder="مثلاً: 58.44" />
                </div>

                {calculationMode !== "molarity" && (
                  <div className="col-md-6 mb-3">
                    <label >المولارية المستهدفة</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={molarity} onChange={(e) => setMolarity(e.target.value)} />
                      <select className="form-select" style={{ maxWidth: "80px" }} value={molarityUnit} onChange={(e) => setMolarityUnit(e.target.value)}>
                        <option value="M">M</option>
                        <option value="mM">mM</option>
                        <option value="uM">μM</option>
                      </select>
                    </div>
                  </div>
                )}

                {calculationMode !== "volume" && (
                  <div className="col-md-6 mb-3">
                    <label >الحجم</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={volume} onChange={(e) => setVolume(e.target.value)} />
                      <select className="form-select" style={{ maxWidth: "80px" }} value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)}>
                        <option value="L">L</option>
                        <option value="mL">mL</option>
                      </select>
                    </div>
                  </div>
                )}

                {calculationMode !== "mass" && (
                  <div className="col-md-6 mb-3">
                    <label >الكتلة / الوزن</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={mass} onChange={(e) => setMass(e.target.value)} />
                      <select className="form-select" style={{ maxWidth: "80px" }} value={massUnit} onChange={(e) => setMassUnit(e.target.value)}>
                        <option value="g">g</option>
                        <option value="mg">mg</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center mt-3">
                <button onClick={handleClear} className="btn btn-secondary px-5"style={{marginRight:'3px'}}>امسح</button>
              </div>

              {error && <div className="alert alert-danger mt-3">{error}</div>}

              {result && (
                <div className="mt-4 result-box">
                  <table className="table bmi-table">
                    <thead>
                      <tr>
                        <th className="bmi-header text-center">النتيجة</th>
                        <th className="bmi-header text-center">القيمة</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center">القيمة المحسوبة</td>
                        <td className="text-center" style={{ fontSize: "20px", fontWeight: "bold", color: "#1a5f3f" }}>
                          {result.value.toFixed(4)} {result.unit}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="alert alert-info text-center" style={{ fontSize: "14px" }}>
                    <strong>طريقة الحساب:</strong> {result.details}
                  </div>
                </div>
              )}
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>ما هي حاسبة المولارية؟</strong></div>
              <div className="info-section-body">
                <p>حاسبة المولارية هي أداة علمية تُستخدم لتحديد تركيز المحلول الكيميائي، والذي يُعبر عنه بالمولارية (M). المولارية هي عدد مولات المادة المذابة في لتر واحد من المحلول.</p>
                <p>تُستخدم هذه الحاسبة بشكل أساسي لـ:</p>
                <ul>
                  <li>تحديد كمية المادة الصلبة (بالجرامات) اللازمة لتحضير محلول بتركيز معين.</li>
                  <li>حساب التركيز النهائي لمحلول عند إذابة كمية معروفة من المادة في حجم معين.</li>
                  <li>تحويل الوحدات بين المليلتر واللتر، وبين المليجرام والجرام والنانومولار.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>استخدامات حاسبة المولارية</strong></div>
              <div className="info-section-body">
                <ul>
                  <li><strong>الأبحاث العلمية:</strong> تحضير الكواشف والمحاليل المنظمة (Buffers) بدقة متناهية.</li>
                  <li><strong>التعليم الأكاديمي:</strong> مساعدة طلاب الكيمياء في حل المسائل المتعلقة بالتركيز والوزن الجزيئي.</li>
                  <li><strong>الصناعات الدوائية:</strong> حساب نسب المواد الفعالة في الأدوية السائلة.</li>
                  <li><strong>التحاليل الطبية:</strong> ضبط تركيز المحاليل المستخدمة في أجهزة التحليل المختبري.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>القوانين الكيميائية المستخدمة</strong></div>
              <div className="info-section-body">
                <p>تعتمد الحاسبة على القوانين الأساسية التالية:</p>
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th>المطلوب</th>
                      <th>القانون</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>المولارية (M)</td>
                      <td>n (عدد المولات) / V (الحجم باللتر)</td>
                    </tr>
                    <tr>
                      <td>الكتلة (جم)</td>
                      <td>M (المولارية) × MW (الوزن الجزيئي) × V (الحجم باللتر)</td>
                    </tr>
                    <tr>
                      <td>عدد المولات (n)</td>
                      <td>m (الكتلة بالجرام) / MW (الوزن الجزيئي)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>أمثلة عملية</strong></div>
              <div className="info-section-body">
                <p><strong>مثال 1:</strong> تحضير محلول ملح الطعام (NaCl) بتركيز 0.5 M في حجم 1 لتر. (الوزن الجزيئي لـ NaCl هو 58.44 جم/مول).</p>
                <p>الكتلة = 0.5 × 58.44 × 1 = 29.22 جرام.</p>
                <p><strong>مثال 2:</strong> إذابة 10 جرام من الجلوكوز (الوزن الجزيئي 180.16) في 500 مل من الماء.</p>
                <p>المولارية = 10 / (180.16 × 0.5) = 0.111 M.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>نصائح للاستخدام الصحيح</strong></div>
              <div className="info-section-body">
                <ul>
                  <li>تأكد دائماً من مطابقة وحدة الحجم (لتر أو مليلتر) قبل البدء بالحساب اليدوي.</li>
                  <li>استخدم الوزن الجزيئي الدقيق للمادة الكيميائية (يمكن العجوز عليه في ملصق العبوة).</li>
                  <li>عند تحضير المحاليل، أضف المادة المذابة أولاً ثم أكمل الحجم بالماء حتى تصل للعلامة المطلوبة لضمان دقة الحجم الكلي.</li>
                  <li>تجنب الخلط بين المولارية (M) والمولالية (m)، حيث تعتمد الأخيرة على كتلة المذيب وليس حجم المحلول.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>الأسئلة الشائعة (FAQ)</strong></div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>ما هي وحدة المولارية؟</strong>
                  <p>وحدة المولارية هي "مول لكل لتر" (mol/L)، ويُرمز لها اختصاراً بالرمز M. على سبيل المثال، محلول 1M يعني وجود مول واحد من المادة في لتر من المحلول.</p>
                </div>
                <div className="mb-3">
                  <strong>هل تتأثر المولارية بدرجة الحرارة؟</strong>
                  <p>نعم، بما أن الحجم يتغير بتغير درجة الحرارة (تمدد السوائل)، فإن المولارية قد تتغير طفيفاً بارتفاع الحرارة، على عكس الكتلة التي تظل ثابتة.</p>
                </div>
                <div className="mb-3">
                  <strong>كيف أحول من مليجرام إلى جرام؟</strong>
                  <p>الجرام الواحد يحتوي على 1000 مليجرام. للتحويل من مليجرام إلى جرام، اقسم القيمة على 1000 (مثلاً: 500 ملجم تصبح 0.5 جرام).</p>
                </div>
                <div className="mb-3">
                  <strong>لماذا أحتاج للوزن الجزيئي؟</strong>
                  <p>الوزن الجزيئي هو الرابط بين الكتلة الفيزيائية (الجرامات) والكمية الكيميائية (المولات). بدونه لا يمكن معرفة عدد الجزيئات الموجودة في وزن معين.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MolarityCalculator;